/**
 * Shared Element Resolver
 *
 * Extracts the element resolution logic used by both cache replay and recording
 * verification into a single shared module. This guarantees that recording
 * verification and replay use the exact same resolution algorithm — if a selector
 * verifies during recording, it will resolve identically during replay.
 *
 * @module workflows/cache/element-resolver
 * @see docs/features/workflow-cache.md
 */

import type { Page, Locator } from 'playwright';
import type { SelectorStrategy, SelectorAuthor } from '@chatsdet/types';
import { createLogger } from './logger.js';
import { evaluateLocatorChain, stripFilterClauses } from './locator-parser.js';
import {
  buildNonQueryableRoleLocator,
  isNonQueryableRole,
  NON_QUERYABLE_ROLES,
  resolveNonQueryableRoleCandidate,
} from './non-queryable-role-resolver.js';

let _logger: ReturnType<typeof createLogger> | null = null;
const logger = () => {
  if (!_logger) {
    _logger = createLogger({ module: 'ElementResolver' });
  }
  return _logger;
};

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Short timeout for element resolution checks (5 seconds).
 * Cache replay should fail fast if elements aren't immediately available.
 */
export const RESOLVE_TIMEOUT_MS = 5000;

/**
 * Longer timeout for visibility waits (15 seconds).
 * During multi-action cache replay, target apps may re-render between actions,
 * causing elements to be briefly hidden during React/framework re-renders.
 * This gives 3x more patience than RESOLVE_TIMEOUT_MS for transient render states.
 */
const VISIBILITY_TIMEOUT_MS = 15000;

/**
 * Quick visibility check before the bounding-box escape hatch (2 seconds).
 * Most elements are visible immediately or within a few hundred ms. If Playwright
 * reports "resolved to hidden" within this window, we check for rendered dimensions
 * before committing to the full VISIBILITY_TIMEOUT_MS wait.
 */
const VISIBILITY_QUICK_CHECK_MS = 2000;

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Locator context — either page or frame locator.
 * Used to support elements inside iframes.
 */
export type LocatorContext = Page | ReturnType<Page['frameLocator']>;

/**
 * Resolution mode:
 * - 'replay': throws on failure (used during cache replay)
 * - 'verify': returns null on failure (used during recording verification)
 */
export type ResolveMode = 'replay' | 'verify';

/**
 * Options for element resolution.
 */
/**
 * Timing info reported by the onTiming callback during element resolution.
 */
export interface ElementResolutionTiming {
  /** Total time in resolveElement */
  totalMs: number;
  /** Time spent in verifyElementReady visibility wait */
  visibilityWaitMs: number;
  /** Time spent waiting for element position to stabilize (animations/transitions) */
  stabilityWaitMs: number;
  /** Which selector strategy resolved the element */
  selectorUsed: string;
  /**
   * Locator author that resolved the element, derived from the strategy label.
   * 'agentic' for the selector-agent-authored Playwright locator (and its
   * filter-stripped fallback); 'deterministic' for role/testId/label/text/css.
   */
  selectorAuthor: SelectorAuthor;
}

/**
 * Classify a resolver strategy label into its locator author. The agentic
 * Playwright locator is always tagged `(agentic)`; its filter-stripped fallback
 * derives from the same author. Everything else (testId/role/label/text/css and
 * the explicit `candidate (deterministic)`) is deterministic.
 */
export function classifySelectorAuthor(selectorName: string): SelectorAuthor {
  return /\(agentic\)/.test(selectorName) || selectorName.includes('filter-stripped')
    ? 'agentic'
    : 'deterministic';
}

export interface ResolveElementOptions {
  /**
   * Resolution mode. Defaults to 'replay'.
   */
  mode?: ResolveMode;
  /**
   * When true, validates that the resolved element is a select/combobox.
   * Falls back to finding select elements near matching text if needed.
   */
  requireSelectElement?: boolean;
  /**
   * Variable values for structural fallback when hydrated selectors fail.
   * Used with dynamicText annotation for a last-resort role+hasText lookup.
   */
  variableValues?: Record<string, string>;
  /**
   * Optional callback reporting timing breakdown after element resolution.
   * Called once when an element is successfully resolved, before returning.
   */
  onTiming?: (timing: ElementResolutionTiming) => void;
  /**
   * The type of action being resolved (e.g. 'click', 'type').
   * When set to 'click', text disambiguation will prefer interactive elements
   * (combobox, button, link, etc.) over labels/static text.
   */
  actionType?: string;
  /**
   * When true, disables fallback heuristics that could produce false-positive matches:
   * multi-match `.first()`, filter stripping, positional index trust, bounding-box
   * escape hatch, and fallback to weaker selectors when playwrightLocator fails.
   * @see docs/features/workflow-cache.md
   */
  strictMode?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Data attribute extraction for disambiguation
// ─────────────────────────────────────────────────────────────────────────────

interface DisambiguationAttrs {
  itemIndex: number;
  rowIndex: number;
  automationId: string | null;
}

/**
 * Batch-extract disambiguation attrs for all matched elements in a single
 * round-trip using evaluateAll(). Returns up to `limit` results.
 *
 * The aria-rowindex walk checks the element itself then walks up to 10
 * ancestors — many grid UIs (BC Dynamics) put `aria-rowindex` on the
 * `<tr>` parent rather than the cell/input.
 */
const extractDisambiguationAttrsBatch = (
  locator: Locator,
  limit: number
): Promise<DisambiguationAttrs[]> =>
  locator.evaluateAll((elements, max) => {
    const getRowIndex = (node: Element): number => {
      let c: Element | null = node;
      let depth = 0;
      while (c && depth < 10) {
        const ri = c.getAttribute('aria-rowindex');
        if (ri) return parseInt(ri, 10);
        c = c.parentElement;
        depth++;
      }
      return NaN;
    };

    return elements.slice(0, max).map((el) => ({
      itemIndex: parseInt(
        el.getAttribute('data-item-index') ?? el.getAttribute('data-selection-index') ?? '',
        10
      ),
      rowIndex: getRowIndex(el),
      automationId: el.getAttribute('data-automationid'),
    }));
  }, Math.min(limit, 10));

// ─────────────────────────────────────────────────────────────────────────────
// Dialog-scoped filtering for modal disambiguation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * When multiple elements match and a visible dialog/modal is open, filter
 * to elements inside the dialog. This prevents sidebar/background elements
 * from being selected when a modal field with the same label is the target.
 *
 * Returns a narrowed locator + count if dialog scoping helped, or null if
 * no visible dialog was found or scoping didn't reduce matches.
 */
const filterToVisibleDialog = async (
  page: Page,
  candidateLocator: Locator,
  currentCount: number
): Promise<{ locator: Locator; count: number } | null> => {
  try {
    // Find visible dialog elements — matches the same roles that discoverParentContext
    // captures at recording time (SEMANTIC_ROLES includes 'dialog' and 'alertdialog')
    const dialogLocator = page.locator(
      '[role="dialog"]:visible, [role="alertdialog"]:visible, dialog[open]'
    );
    const dialogCount = await Promise.race([
      dialogLocator.count(),
      new Promise<number>((_, reject) =>
        setTimeout(() => reject(new Error('Dialog check timed out')), 2000)
      ),
    ]);

    if (dialogCount === 0) return null;

    // Use the first visible dialog as the scoping container
    const dialog = dialogLocator.first();
    const scopedLocator = dialog.locator(candidateLocator);
    const scopedCount = await Promise.race([
      scopedLocator.count(),
      new Promise<number>((_, reject) =>
        setTimeout(() => reject(new Error('Dialog scoped count timed out')), 2000)
      ),
    ]);

    // Only useful if we narrowed the set
    if (scopedCount > 0 && scopedCount < currentCount) {
      logger().debug('[ElementResolver] Dialog scoping narrowed matches', {
        originalCount: currentCount,
        scopedCount,
      });
      return { locator: scopedLocator, count: scopedCount };
    }

    return null;
  } catch {
    return null;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Role compatibility for text fallback validation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Map of expected roles to compatible actual roles.
 * Used to validate text-fallback matches when the primary role selector failed.
 * Handles common patterns like Dynamics 365 combobox exposing as textbox.
 * Unmapped roles are permissive (no validation applied).
 */
const COMPATIBLE_ROLES: Record<string, Set<string>> = {
  combobox: new Set(['combobox', 'textbox', 'searchbox', 'listbox']),
  textbox: new Set(['textbox', 'combobox', 'searchbox']),
  searchbox: new Set(['searchbox', 'textbox', 'combobox']),
  listbox: new Set(['listbox', 'combobox']),
  button: new Set(['button', 'menuitem', 'link', 'tab']),
  link: new Set(['link', 'button']),
  checkbox: new Set(['checkbox', 'switch']),
  switch: new Set(['switch', 'checkbox']),
};

/**
 * Implicit ARIA role mapping for common HTML tags.
 */
const TAG_TO_IMPLICIT_ROLE: Record<string, string> = {
  input: 'textbox',
  textarea: 'textbox',
  select: 'listbox',
  button: 'button',
  a: 'link',
};

/**
 * Roles considered interactive for click-action disambiguation.
 * When text fallback matches multiple elements and the action is a click,
 * we prefer elements with these roles over labels/static text.
 */
const INTERACTIVE_ROLES = new Set([
  'button',
  'link',
  'combobox',
  'menuitem',
  'checkbox',
  'switch',
  'tab',
  'textbox',
  'searchbox',
  'option',
  'menuitemcheckbox',
  'menuitemradio',
  'radio',
  'spinbutton',
]);

/**
 * Interactive HTML tags (implicit interactive role via tag name).
 */
const INTERACTIVE_TAGS = new Set(['button', 'a', 'input', 'select', 'textarea']);

/**
 * Check whether an element has an interactive role (explicit or implicit).
 * Used during text disambiguation to prefer clickable elements over labels.
 */
export const isInteractiveElement = async (locator: Locator): Promise<boolean> => {
  try {
    const { role, tag } = await locator.evaluate((el) => ({
      role: el.getAttribute('role'),
      tag: el.tagName.toLowerCase(),
    }));
    if (role && INTERACTIVE_ROLES.has(role)) return true;
    if (INTERACTIVE_TAGS.has(tag)) return true;
    const implicitRole = TAG_TO_IMPLICIT_ROLE[tag];
    if (implicitRole && INTERACTIVE_ROLES.has(implicitRole)) return true;
    return false;
  } catch {
    return false;
  }
};

/**
 * Roles considered typeable for type-action disambiguation.
 * When text fallback matches multiple elements and the action is a type,
 * we prefer elements with these roles over labels/static text.
 */
const TYPEABLE_ROLES = new Set(['textbox', 'searchbox', 'spinbutton', 'combobox']);
const TYPEABLE_TAGS = new Set(['input', 'textarea', 'select']);

/**
 * Check whether an element is typeable (can receive keyboard input).
 * Used during text disambiguation to prefer input fields over labels for type actions.
 */
const isTypeableElement = async (locator: Locator): Promise<boolean> => {
  try {
    const { role, tag, contentEditable } = await locator.evaluate((el) => ({
      role: el.getAttribute('role'),
      tag: el.tagName.toLowerCase(),
      contentEditable: (el as HTMLElement).isContentEditable,
    }));
    if (contentEditable) return true;
    if (role && TYPEABLE_ROLES.has(role)) return true;
    if (TYPEABLE_TAGS.has(tag)) return true;
    return false;
  } catch {
    return false;
  }
};

/**
 * Check whether a resolved element's actual role is compatible with the expected role.
 *
 * - Returns `true` if the element has a compatible role (explicit or implicit).
 * - Returns `false` if the element has no discernible role (generic div/span).
 * - Returns `true` for unmapped expected roles (permissive — no validation).
 * - Returns `true` on evaluation errors (fail-open to avoid false rejections).
 */
export const isRoleCompatible = async (
  locator: Locator,
  expectedRole: string
): Promise<boolean> => {
  const compatibleSet = COMPATIBLE_ROLES[expectedRole];
  if (!compatibleSet) {
    // Unmapped role — no validation, allow through
    return true;
  }

  try {
    const { role, tag } = await locator.evaluate((el) => ({
      role: el.getAttribute('role'),
      tag: el.tagName.toLowerCase(),
    }));

    const actualRole = role ?? TAG_TO_IMPLICIT_ROLE[tag];

    if (!actualRole) {
      // No discernible role (generic div/span) — reject
      return false;
    }

    return compatibleSet.has(actualRole);
  } catch {
    // Evaluation failed — fail-open
    return true;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Text normalization helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalize text used for selector matching.
 * Strips private use glyphs, normalizes whitespace.
 */
export const normalizeMatchText = (value: string): string => {
  return value
    .replace(/[\uE000-\uF8FF]/g, '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Extract the primary text from a concatenated accessible name.
 * E.g., "Sales Quotes Lists Not bookmarked" -> "Sales Quotes"
 *
 * Common patterns in concatenated names:
 * - "Primary Text Lists/Reports/etc"
 * - "Primary Text Not bookmarked/Bookmarked"
 */
export const extractPrimaryText = (fullText: string): string | null => {
  if (!fullText) return null;

  const normalized = normalizeMatchText(fullText);
  if (!normalized) return null;

  // Common suffixes that indicate concatenated text
  const suffixPatterns = [
    /\s+(Lists?|Reports?|Tasks?|Pages?|Archive)\b.*$/i,
    /\s+Not bookmarked$/i,
    /\s+Bookmarked$/i,
    /\s+-\s+Microsoft.*$/i,
  ];

  let primaryText = normalized;
  for (const pattern of suffixPatterns) {
    primaryText = primaryText.replace(pattern, '');
  }

  // Only return if we actually shortened it and have something meaningful left
  if (primaryText !== normalized && primaryText.trim().length >= 3) {
    return primaryText.trim();
  }

  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Element helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Find the first visible element from a locator that matches multiple elements.
 * Returns the locator for the first visible element, or null if none are visible.
 */
export const findFirstVisibleElement = async (
  locator: Locator,
  count: number,
  timeoutMs: number = RESOLVE_TIMEOUT_MS
): Promise<Locator | null> => {
  const limit = Math.min(count, 10);

  // Check all candidates in parallel instead of sequentially
  const results = await Promise.all(
    Array.from({ length: limit }, (_, i) => {
      const nth = locator.nth(i);
      return Promise.race([
        nth.isVisible(),
        new Promise<boolean>((_, reject) =>
          setTimeout(() => reject(new Error('Visibility check timed out')), timeoutMs)
        ),
      ]).catch(() => false);
    })
  );

  const firstVisibleIndex = results.indexOf(true);
  if (firstVisibleIndex >= 0) {
    logger().debug('[ElementResolver] Found visible element at index', { index: firstVisibleIndex });
    return locator.nth(firstVisibleIndex);
  }
  return null;
};

/**
 * Bounding-box escape hatch: check if an element has real rendered dimensions
 * despite Playwright reporting it as "hidden". Returns true if the element
 * should be accepted as usable, false otherwise.
 *
 * This handles CSS edge cases (ancestor opacity:0 during transitions,
 * visibility:hidden overridden by child, clip-path, transparent overlays)
 * where the element is visually present and interactable.
 */
const checkBoundingBoxEscapeHatch = async (
  locator: Locator,
  selectorDesc: string
): Promise<boolean> => {
  try {
    const diagnostics = await locator.evaluate((el) => {
      const cs = window.getComputedStyle(el);
      const r = el.getBoundingClientRect();
      const ancestors: Array<{
        tag: string;
        role: string | null;
        vis: string;
        disp: string;
        opa: string;
        h: number;
      }> = [];
      let parent = el.parentElement;
      while (parent && ancestors.length < 10) {
        const pcs = window.getComputedStyle(parent);
        const pr = parent.getBoundingClientRect();
        if (
          pcs.visibility === 'hidden' ||
          pcs.display === 'none' ||
          pcs.opacity === '0' ||
          pr.height === 0
        ) {
          ancestors.push({
            tag: parent.tagName,
            role: parent.getAttribute('role'),
            vis: pcs.visibility,
            disp: pcs.display,
            opa: pcs.opacity,
            h: Math.round(pr.height),
          });
        }
        parent = parent.parentElement;
      }
      return {
        visibility: cs.visibility,
        display: cs.display,
        opacity: cs.opacity,
        width: Math.round(r.width),
        height: Math.round(r.height),
        hiddenAncestors: ancestors,
      };
    });

    if (diagnostics.width > 0 && diagnostics.height > 0 && diagnostics.display !== 'none') {
      logger().warn(
        '[ElementResolver] Element reported hidden but has rendered dimensions, accepting',
        {
          selectorDesc,
          width: diagnostics.width,
          height: diagnostics.height,
          visibility: diagnostics.visibility,
          opacity: diagnostics.opacity,
          hiddenAncestorCount: diagnostics.hiddenAncestors.length,
        }
      );
      return true;
    }

    logger().warn('[ElementResolver] Element hidden diagnostics', {
      selectorDesc,
      diagnostics,
    });
    return false;
  } catch {
    // Element may have been detached — best-effort
    return false;
  }
};

/** Helper: returns true when the error message indicates the element was found but hidden */
const isResolvedToHidden = (message: string): boolean =>
  message.includes('resolved to hidden') || message.includes('not visible');

// ─────────────────────────────────────────────────────────────────────────────
// Position Stability
// ─────────────────────────────────────────────────────────────────────────────

/** Maximum time to wait for an element's position to stabilize */
const STABILITY_MAX_WAIT_MS = 1000;
/** Threshold in pixels — movement below this is considered stable */
const STABILITY_THRESHOLD_PX = 1;

/**
 * Wait until an element's bounding box stops moving.
 *
 * Catches CSS animations/transitions completing on dropdown menus, popovers,
 * and slide-in panels. Uses a single in-browser rAF loop instead of polling
 * via CDP round-trips (~20 round-trips → 1).
 *
 * @returns milliseconds spent waiting for stability
 */
const waitForPositionStability = async (
  locator: Locator,
  selectorDesc: string
): Promise<number> => {
  const startMs = Date.now();
  try {
    const result = await locator.evaluate(
      (el, { maxWaitMs, thresholdPx }) => {
        return new Promise<{ elapsed: number; stable: boolean }>((resolve) => {
          const start = performance.now();
          let prevRect = el.getBoundingClientRect();
          // Require 3 consecutive stable frames (~48ms at 60fps) to match
          // the spirit of the old 50ms polling interval and avoid false
          // positives from easing curves that briefly plateau.
          let stableFrames = 0;
          const REQUIRED_STABLE_FRAMES = 3;

          const check = () => {
            const elapsed = performance.now() - start;
            if (elapsed >= maxWaitMs) {
              resolve({ elapsed: Math.round(elapsed), stable: false });
              return;
            }

            const currRect = el.getBoundingClientRect();
            const dx = Math.abs(currRect.x - prevRect.x);
            const dy = Math.abs(currRect.y - prevRect.y);
            const dw = Math.abs(currRect.width - prevRect.width);
            const dh = Math.abs(currRect.height - prevRect.height);

            if (
              dx <= thresholdPx &&
              dy <= thresholdPx &&
              dw <= thresholdPx &&
              dh <= thresholdPx
            ) {
              stableFrames++;
              if (stableFrames >= REQUIRED_STABLE_FRAMES) {
                resolve({ elapsed: Math.round(elapsed), stable: true });
                return;
              }
            } else {
              stableFrames = 0;
            }

            prevRect = currRect;
            requestAnimationFrame(check);
          };

          // Wait one frame before first comparison
          requestAnimationFrame(check);
        });
      },
      { maxWaitMs: STABILITY_MAX_WAIT_MS, thresholdPx: STABILITY_THRESHOLD_PX }
    );

    const elapsed = Date.now() - startMs;
    if (!result.stable) {
      logger().warn('[ElementResolver] Element position did not stabilize within timeout', {
        selectorDesc,
        stabilityWaitMs: elapsed,
      });
    } else if (result.elapsed > 60) {
      logger().debug('[ElementResolver] Element position stabilized', {
        selectorDesc,
        stabilityWaitMs: elapsed,
      });
    }
    return elapsed;
  } catch {
    // Element detached or navigated away — not worth blocking
    return Date.now() - startMs;
  }
};

/**
 * Verify an element is visible and attached to DOM before returning.
 *
 * Uses a two-phase approach:
 * 1. Quick visibility check (2s) — catches most elements that are immediately visible.
 * 2. If the element was found but Playwright reports "hidden", run a bounding-box
 *    escape hatch — if the element has real rendered dimensions it's accepted
 *    immediately (~2s total instead of 15s).
 * 3. If the escape hatch doesn't apply (element genuinely has no dimensions),
 *    wait the remaining time for it to become visible (covers React re-renders,
 *    lazy-loaded content, CSS transitions that are legitimately completing).
 *
 * @param locator - The element locator
 * @param selectorDesc - Description of the selector for error messages
 * @param strict - If true (default), throws on timeout. If false, only logs warning.
 */
export const verifyElementReady = async (
  locator: Locator,
  selectorDesc: string,
  strict = true,
  onStabilityWait?: (ms: number) => void,
  strictMode = false
): Promise<boolean> => {
  /** Wait for position stability and report timing before returning ready */
  const stableReturn = async (): Promise<true> => {
    const ms = await waitForPositionStability(locator, selectorDesc);
    onStabilityWait?.(ms);
    return true;
  };

  // ── Phase 1: Quick visibility check ──
  try {
    await locator.waitFor({ state: 'visible', timeout: VISIBILITY_QUICK_CHECK_MS });
    return stableReturn();
  } catch (quickErr) {
    const quickMsg = quickErr instanceof Error ? quickErr.message : String(quickErr);

    // Element exists but Playwright says "hidden" — try bounding-box escape hatch
    // before committing to a long wait.
    // V13: In strict mode, skip the escape hatch — trust Playwright's visibility check.
    if (isResolvedToHidden(quickMsg)) {
      if (strictMode) {
        logger().warn('[ElementResolver] [strict] Skipping bounding-box escape hatch', {
          strictMode: true,
          rejection: 'escape_hatch_skipped',
          selectorDesc,
        });
      } else if (await checkBoundingBoxEscapeHatch(locator, selectorDesc)) {
        return stableReturn();
      }
      // Element genuinely has no rendered dimensions — fall through to extended wait
      logger().debug(
        '[ElementResolver] Element hidden with no dimensions after quick check, continuing wait',
        {
          selectorDesc,
        }
      );
    }
  }

  // ── Phase 2: Extended wait for elements that need more time to appear ──
  // Covers: elements hidden during React/framework re-renders, lazy-loaded content, etc.
  try {
    const remainingMs = Math.max(VISIBILITY_TIMEOUT_MS - VISIBILITY_QUICK_CHECK_MS, 1000);
    await locator.waitFor({ state: 'visible', timeout: remainingMs });
    return stableReturn();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);

    // Final escape hatch — element may have appeared during the wait but
    // still reports "hidden" due to CSS issues.
    // V13: In strict mode, skip the escape hatch.
    if (isResolvedToHidden(message)) {
      if (!strictMode && await checkBoundingBoxEscapeHatch(locator, selectorDesc)) {
        return stableReturn();
      }
    }

    if (strict) {
      throw new Error(`Element not ready (${selectorDesc}): ${message}`);
    }
    logger().debug('[ElementResolver] Element visibility check failed (non-strict)', {
      selectorDesc,
      error: message,
    });
    return false;
  }
};

/**
 * Validate that a locator points to a select element.
 * Returns true if it's a select/combobox, false otherwise.
 */
export const isSelectElement = async (locator: Locator): Promise<boolean> => {
  try {
    const { tagName, role } = await locator.evaluate((el) => ({
      tagName: el.tagName.toLowerCase(),
      role: el.getAttribute('role'),
    }));
    return tagName === 'select' || role === 'combobox' || role === 'listbox';
  } catch {
    return false;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Frame locator helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get all frame locators from a page for searching within iframes.
 * Uses specific selectors to avoid strict mode violations when multiple iframes exist.
 */
export const getFrameLocators = (
  page: Page
): Array<{ name: string; locator: ReturnType<Page['frameLocator']> }> => {
  return [
    { name: 'iframe:main', locator: page.frameLocator('iframe.designer-client-frame') },
    { name: 'iframe:first', locator: page.frameLocator('iframe').first() },
    { name: 'iframe:nth(0)', locator: page.frameLocator('iframe').nth(0) },
    { name: 'iframe:nth(1)', locator: page.frameLocator('iframe').nth(1) },
  ];
};

// ─────────────────────────────────────────────────────────────────────────────
// Lightweight resolution (for overlay checking)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lightweight element resolution for overlay checking (no verification).
 * Only tries fast selector strategies to minimize overhead.
 */
export const resolveElementForCheck = async (
  page: Page,
  selectors: SelectorStrategy
): Promise<Locator | null> => {
  try {
    if (selectors.testId) {
      const loc = page.getByTestId(selectors.testId);
      if ((await loc.count()) > 0) return loc.first();
    }
    if (selectors.role) {
      const loc = page.getByRole(
        selectors.role.role as Parameters<Page['getByRole']>[0],
        selectors.role.name ? { name: selectors.role.name } : undefined
      );
      if ((await loc.count()) > 0) return loc.first();
    }
  } catch {
    // Ignore errors - this is best-effort
  }
  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Parent-scoped context helper
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a list of parent-scoped search contexts for a given page/frame context.
 * When parentContext is available, returns scoped contexts first (role chain, CSS),
 * then the unscoped context as a final fallback. This ensures text/label selectors
 * resolve within the correct container (e.g., dialog) before searching the full page.
 */
type ParentContextChainNode = {
  role: string;
  name?: string;
  css?: string;
  nameFromHeading?: boolean;
  containsText?: string;
};

type ParentContextInput = {
  role?: string;
  name?: string;
  css?: string;
  nameFromHeading?: boolean;
  containsText?: string;
  parentChain?: ParentContextChainNode[];
};

function getParentScopedContexts(
  context: LocatorContext,
  parentContext: ParentContextInput | undefined
): Array<{ searchCtx: LocatorContext | Locator; scopeLabel: string }> {
  if (!parentContext) return [{ searchCtx: context, scopeLabel: '' }];

  const result: Array<{ searchCtx: LocatorContext | Locator; scopeLabel: string }> = [];

  if (parentContext.parentChain?.length) {
    let parent: Locator | undefined;
    for (const ancestor of [...parentContext.parentChain].reverse()) {
      let ancestorLocator: Locator =
        ancestor.nameFromHeading && ancestor.name
          ? context
              .getByRole(ancestor.role as Parameters<Page['getByRole']>[0])
              .filter({ has: context.getByRole('heading', { name: ancestor.name }) })
          : context.getByRole(
              ancestor.role as Parameters<Page['getByRole']>[0],
              ancestor.name ? { name: ancestor.name } : undefined
            );
      if (ancestor.containsText) {
        ancestorLocator = ancestorLocator.filter({ hasText: ancestor.containsText });
      }
      parent = parent ? parent.locator(ancestorLocator) : ancestorLocator;
    }
    if (parent) result.push({ searchCtx: parent, scopeLabel: ` [parent-chain]` });
  } else if (parentContext.role) {
    let parentLocator: Locator =
      parentContext.nameFromHeading && parentContext.name
        ? context
            .getByRole(parentContext.role as Parameters<Page['getByRole']>[0])
            .filter({ has: context.getByRole('heading', { name: parentContext.name }) })
        : context.getByRole(
            parentContext.role as Parameters<Page['getByRole']>[0],
            parentContext.name ? { name: parentContext.name } : undefined
          );
    if (parentContext.containsText) {
      parentLocator = parentLocator.filter({ hasText: parentContext.containsText });
    }
    const label = parentContext.containsText
      ? ` [parent:${parentContext.role}~"${parentContext.containsText}"]`
      : ` [parent:${parentContext.role}]`;
    result.push({ searchCtx: parentLocator, scopeLabel: label });
  }

  if (parentContext.css) {
    result.push({ searchCtx: context.locator(parentContext.css), scopeLabel: ` [parent-css]` });
  }

  // Always include unscoped as final fallback
  result.push({ searchCtx: context, scopeLabel: '' });

  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Role selector resolution
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Try to find an element using role selector within a context (page or frame).
 * Returns the locator if found, null otherwise.
 *
 * Attempts in order:
 * 1. Full name with parent context
 * 2. Full name without parent context
 * 3. Partial/primary text extracted from name (for concatenated accessible names)
 *
 * Non-queryable roles (`generic`/`presentation`/`none`) resolve through a
 * tag approximation filtered by the strategy's containsText chain, then a
 * unique-INNERMOST-match requirement: the substrate's exact-child-text-set
 * uniqueness means every ancestor of the target also contains the target's
 * texts, so the innermost match is the authored element. No text anchor, or
 * more than one innermost match, fails closed — never ordinal fallback.
 */
export const tryRoleSelector = async (
  context: LocatorContext,
  selectors: SelectorStrategy,
  contextName: string,
  strictMode = false
): Promise<{ locator: Locator; selectorDesc: string } | null> => {
  if (!selectors.role) return null;

  const { role, name, exact } = selectors.role;
  const nonQueryableRole = isNonQueryableRole(role);
  const normalizedName = name ? normalizeMatchText(name) : undefined;
  const textFilterRoles = new Set([
    'row',
    'listitem',
    'option',
    'menuitem',
    'treeitem',
    'gridcell',
    'article',
  ]);
  const normalizedContainsText = selectors.containsText?.map((part) => normalizeMatchText(part));
  const containsTextFallback =
    !normalizedContainsText && role && textFilterRoles.has(role) && normalizedName
      ? [normalizedName]
      : undefined;
  const activeContainsText = normalizedContainsText ?? containsTextFallback;

  const applyContainsText = (locator: Locator): Locator => {
    if (!activeContainsText || activeContainsText.length === 0) return locator;
    let filtered = locator;
    for (const part of activeContainsText) {
      if (!part) continue;
      filtered = filtered.filter({ hasText: part });
    }
    return filtered;
  };

  /**
   * Helper to try a specific name value and return locator if found.
   */
  const tryWithName = async (
    searchName: string | undefined,
    variant: string
  ): Promise<{ locator: Locator; selectorDesc: string } | null> => {
    const locatorOpts = searchName ? { name: searchName, exact: exact ?? false } : undefined;
    const baseLocator = nonQueryableRole
      ? buildNonQueryableRoleLocator(context, role, activeContainsText)
      : context.getByRole(role as Parameters<Page['getByRole']>[0], locatorOpts);
    if (!baseLocator) return null;

    const locator = nonQueryableRole ? baseLocator : applyContainsText(baseLocator);

    // Build candidate locator scopes in precedence order:
    // 1) parent role/chain (if present)
    // 2) parent CSS (if present)
    // 3) no parent fallback
    const candidates: Array<{ scopedLocator: Locator; suffix: string }> = [];
    const hasParentScope = !!(
      selectors.parentContext?.parentChain?.length ||
      selectors.parentContext?.role ||
      selectors.parentContext?.css
    );

    if (selectors.parentContext?.parentChain?.length) {
      let parentScoped = locator;
      for (const ancestor of [...selectors.parentContext.parentChain].reverse()) {
        let parentLocator: Locator;
        if (ancestor.nameFromHeading && ancestor.name) {
          parentLocator = context
            .getByRole(ancestor.role as Parameters<Page['getByRole']>[0])
            .filter({ has: context.getByRole('heading', { name: ancestor.name }) });
        } else {
          parentLocator = context.getByRole(
            ancestor.role as Parameters<Page['getByRole']>[0],
            ancestor.name ? { name: ancestor.name } : undefined
          );
        }
        if (ancestor.containsText) {
          parentLocator = parentLocator.filter({ hasText: ancestor.containsText });
        }
        parentScoped = parentLocator.locator(parentScoped);
      }
      candidates.push({ scopedLocator: parentScoped, suffix: '' });
    } else if (selectors.parentContext?.role) {
      let parentLocator: Locator;
      if (selectors.parentContext.nameFromHeading && selectors.parentContext.name) {
        parentLocator = context
          .getByRole(selectors.parentContext.role as Parameters<Page['getByRole']>[0])
          .filter({ has: context.getByRole('heading', { name: selectors.parentContext.name }) });
      } else {
        parentLocator = context.getByRole(
          selectors.parentContext.role as Parameters<Page['getByRole']>[0],
          selectors.parentContext.name ? { name: selectors.parentContext.name } : undefined
        );
      }
      if (selectors.parentContext.containsText) {
        parentLocator = parentLocator.filter({ hasText: selectors.parentContext.containsText });
      }
      candidates.push({ scopedLocator: parentLocator.locator(locator), suffix: '' });
    }

    if (selectors.parentContext?.css) {
      const cssParentLocator = context.locator(selectors.parentContext.css);
      candidates.push({
        scopedLocator: cssParentLocator.locator(locator),
        suffix: ' - css parent',
      });
    }

    if (hasParentScope) {
      candidates.push({ scopedLocator: locator, suffix: ' - no parent' });
    } else {
      candidates.push({ scopedLocator: locator, suffix: '' });
    }

    // Check element count with timeout protection
    try {
      for (const candidate of candidates) {
        const count = await Promise.race([
          candidate.scopedLocator.count(),
          new Promise<number>((_, reject) =>
            setTimeout(() => reject(new Error('Count check timed out')), RESOLVE_TIMEOUT_MS)
          ),
        ]);

        if (count === 0) {
          continue;
        }

        const selectorDesc = `Role selector (${role}: "${searchName ?? ''}"${candidate.suffix}${variant ? ` ${variant}` : ''}${contextName ? ` in ${contextName}` : ''})`;

        if (nonQueryableRole) {
          const resolved = await resolveNonQueryableRoleCandidate({
            scopedLocator: candidate.scopedLocator,
            selectorDesc,
            role,
            count,
            contextName,
            onAmbiguous: (match) =>
              logger().debug(
                '[ElementResolver] Ambiguous innermost match for non-queryable role',
                { role: match.role, count: match.count, context: match.context }
              ),
          });
          if (resolved) return resolved;
          continue;
        }

        if (count === 1) {
          return { locator: candidate.scopedLocator.first(), selectorDesc };
        }

        // Multiple matches - try disambiguation strategies
        logger().debug('[ElementResolver] Multiple role matches, trying disambiguation', {
          role,
          name: searchName,
          count,
          context: contextName,
          hasItemIndex: selectors.itemIndex !== undefined,
          hasRowIndex: selectors.rowIndex !== undefined,
          hasMatchIndex: selectors.parentContext?.matchIndex !== undefined,
        });

        // Strategy 1: Try data attribute matching (most deterministic)
        if (
          selectors.itemIndex !== undefined ||
          selectors.rowIndex !== undefined ||
          selectors.automationId
        ) {
          try {
            const allAttrs = await extractDisambiguationAttrsBatch(candidate.scopedLocator, count);
            for (let i = 0; i < allAttrs.length; i++) {
              const attrs = allAttrs[i];
              const matches =
                (selectors.itemIndex !== undefined && attrs.itemIndex === selectors.itemIndex) ||
                (selectors.rowIndex !== undefined && attrs.rowIndex === selectors.rowIndex) ||
                (selectors.automationId && attrs.automationId === selectors.automationId);

              if (matches) {
                logger().debug('[ElementResolver] Found element by data attributes', {
                  index: i,
                  attrs,
                });
                return {
                  locator: candidate.scopedLocator.nth(i),
                  selectorDesc: `${selectorDesc} [data-attr]`,
                };
              }
            }
          } catch {
            // Fall through to next strategy
          }
        }

        // Strategy 2: Use matchIndex fallback
        if (selectors.parentContext?.matchIndex !== undefined) {
          const matchIndex = selectors.parentContext.matchIndex;
          if (matchIndex < count) {
            const indexed = candidate.scopedLocator.nth(matchIndex);

            // Verify with text content if available
            if (selectors.containsText?.length) {
              try {
                const text = await indexed.textContent();
                const hasExpectedText = selectors.containsText.every((t) => text?.includes(t));
                if (hasExpectedText) {
                  logger().debug(
                    '[ElementResolver] Found element by matchIndex with text verification',
                    {
                      matchIndex,
                    }
                  );
                  return {
                    locator: indexed,
                    selectorDesc: `${selectorDesc} [index:${matchIndex}]`,
                  };
                }
              } catch {
                // V4: In strict mode, if text verification fails, don't trust the index
                if (strictMode) {
                  logger().warn('[ElementResolver] [strict] matchIndex text verification failed, rejecting', {
                    strictMode: true,
                    rejection: 'index_fallback',
                    matchIndex,
                    role,
                    name: searchName,
                  });
                  return null;
                }
                // Fall through to use index anyway
              }
            }

            // V4: In strict mode, don't trust bare positional index without text verification
            if (strictMode) {
              logger().warn('[ElementResolver] [strict] Rejecting matchIndex without text verification', {
                strictMode: true,
                rejection: 'index_fallback',
                matchIndex,
                role,
                name: searchName,
                count,
              });
              return null;
            }

            // Trust the index
            logger().debug('[ElementResolver] Using matchIndex fallback', { matchIndex });
            return { locator: indexed, selectorDesc: `${selectorDesc} [index:${matchIndex}]` };
          }
        }

        // No disambiguation succeeded - treat as selector miss
        logger().warn('[ElementResolver] No disambiguation succeeded, treating as selector miss', {
          role,
          name: searchName,
          count,
        });
        return null;
      }
    } catch (err) {
      logger().debug('[ElementResolver] Role selector variant failed', {
        context: contextName,
        variant,
        searchName,
        error: err instanceof Error ? err.message : String(err),
      });
    }

    return null;
  };

  // Try 1: Full name (original)
  const fullResult = await tryWithName(normalizedName ?? name, '');
  if (fullResult) return fullResult;

  // Try role-only with containsText if name match failed
  if (activeContainsText && activeContainsText.length > 0) {
    const roleOnlyResult = await tryWithName(undefined, '[containsText]');
    if (roleOnlyResult) return roleOnlyResult;
  }

  // Try 2: Extract primary text from concatenated name
  if (name) {
    const primaryText = extractPrimaryText(normalizedName ?? name);
    if (primaryText) {
      logger().debug('[ElementResolver] Trying partial text match', {
        role,
        originalName: normalizedName ?? name,
        primaryText,
        context: contextName,
      });
      const partialResult = await tryWithName(primaryText, '[partial]');
      if (partialResult) return partialResult;
    }
  }

  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main element resolution
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolve an element using selector fallback chain.
 *
 * Tries selectors in priority order:
 * 1. data-testid
 * 2. Role + name (most stable)
 * 3. Label text (getByLabel)
 * 4. Placeholder text
 * 5. Text content (with label fallback for selects)
 * 6. CSS selector (fallback)
 *
 * Also searches within iframes if element not found on main page.
 * Before returning, verifies the element is visible and attached.
 *
 * Supports two modes:
 * - 'replay' (default): throws Error if element cannot be found
 * - 'verify': returns null if element cannot be found (for recording verification)
 *
 * @param page - Playwright page
 * @param selectors - Selector strategy from cached action
 * @param _timeoutMs - Timeout for locator operations (unused, using RESOLVE_TIMEOUT_MS)
 * @param options - Optional configuration
 * @returns Locator for the element (or null in verify mode)
 * @throws Error if element cannot be found (replay mode only)
 */
export async function resolveElement(
  page: Page,
  selectors: SelectorStrategy,
  _timeoutMs: number,
  options?: ResolveElementOptions
): Promise<Locator>;
export async function resolveElement(
  page: Page,
  selectors: SelectorStrategy,
  _timeoutMs: number,
  options: ResolveElementOptions & { mode: 'verify' }
): Promise<Locator | null>;
export async function resolveElement(
  page: Page,
  selectors: SelectorStrategy,
  _timeoutMs: number,
  options?: ResolveElementOptions
): Promise<Locator | null> {
  const resolveStartMs = Date.now();
  const mode = options?.mode ?? 'replay';
  const actionType = options?.actionType;
  const strictMode = options?.strictMode ?? false;
  const errors: string[] = [];
  const requireSelect = options?.requireSelectElement ?? false;
  const elementTypeName = requireSelect ? 'select element' : 'element';

  logger().debug('[ElementResolver] Resolving element', {
    mode,
    hasTestId: !!selectors.testId,
    testId: selectors.testId,
    hasRole: !!selectors.role,
    role: selectors.role?.role,
    name: selectors.role?.name,
    hasAriaLabel: !!selectors.ariaLabel,
    hasPlaceholder: !!selectors.placeholder,
    hasText: !!selectors.text,
    hasContainsText: !!selectors.containsText?.length,
    hasParentContext: !!selectors.parentContext,
    parentRole: selectors.parentContext?.role,
    hasFrameContext: !!selectors.frameContext,
    frameSelector: selectors.frameContext?.selector,
  });

  /**
   * Helper to validate element if select is required, then verify visibility.
   * Returns the locator if valid, null if validation fails.
   */
  const validateAndReturn = async (
    locator: Locator,
    selectorName: string
  ): Promise<Locator | null> => {
    let capturedStabilityMs = 0;
    const onStabilityWait = (ms: number) => {
      capturedStabilityMs = ms;
    };

    if (requireSelect) {
      if (await isSelectElement(locator)) {
        const visStart = Date.now();
        await verifyElementReady(locator, selectorName, true, onStabilityWait, strictMode);
        const visibilityWaitMs = Date.now() - visStart;
        options?.onTiming?.({
          totalMs: Date.now() - resolveStartMs,
          visibilityWaitMs,
          stabilityWaitMs: capturedStabilityMs,
          selectorUsed: selectorName,
          selectorAuthor: classifySelectorAuthor(selectorName),
        });
        logger().debug('[ElementResolver] Resolved select element', { selector: selectorName });
        return locator;
      }
      errors.push(`${selectorName} found element but it's not a select`);
      return null;
    }
    const visStart = Date.now();
    await verifyElementReady(locator, selectorName, true, onStabilityWait, strictMode);
    const visibilityWaitMs = Date.now() - visStart;
    options?.onTiming?.({
      totalMs: Date.now() - resolveStartMs,
      visibilityWaitMs,
      stabilityWaitMs: capturedStabilityMs,
      selectorUsed: selectorName,
      selectorAuthor: classifySelectorAuthor(selectorName),
    });
    logger().debug('[ElementResolver] Resolved element', { selector: selectorName });
    return locator;
  };

  // Build list of contexts to search
  const contexts: Array<{ name: string; context: LocatorContext }> = [];

  if (selectors.frameContext) {
    const frameSelector = selectors.frameContext.selector;
    const frameName = selectors.frameContext.name ?? selectors.frameContext.id ?? frameSelector;
    logger().debug('[ElementResolver] Using recorded frame context', {
      frameSelector,
      frameType: selectors.frameContext.type,
    });
    contexts.push({
      name: `recorded-frame:${frameName}`,
      context: page.frameLocator(frameSelector),
    });
    // Fallback: also try generic frame locators in case recorded selector is stale
    for (const { name, locator } of getFrameLocators(page)) {
      // Skip the main iframe shortcut when the recorded selector already targets it
      if (name === 'iframe:main' && frameSelector === 'iframe.designer-client-frame') continue;
      contexts.push({ name, context: locator });
    }
  } else {
    contexts.push({ name: 'page', context: page });
    for (const { name, locator } of getFrameLocators(page)) {
      contexts.push({ name, context: locator });
    }
  }

  const hasAlternatePlaywrightLocator = Boolean(
    selectors.playwrightLocatorCandidates &&
      Object.values(selectors.playwrightLocatorCandidates).some(
        (candidate) =>
          typeof candidate?.locator === 'string' &&
          candidate.locator.trim().length > 0 &&
          candidate.locator !== selectors.playwrightLocator
      )
  );

  let primaryPlaywrightLocatorForRetry: Locator | null = null;
  let shouldRetryPrimaryAfterCandidates = false;
  const retryPrimaryPlaywrightLocator = async (
    locator: Locator,
    locatorSource: string
  ): Promise<Locator | null> => {
    // Retry original locator with short poll to allow async auto-fill to propagate.
    const RETRY_TIMEOUT_MS = 2000;
    const RETRY_POLL_MS = 200;
    const retryStart = Date.now();
    while (Date.now() - retryStart < RETRY_TIMEOUT_MS) {
      await new Promise((r) => setTimeout(r, RETRY_POLL_MS));
      try {
        const retryCount = await locator.count();
        if (retryCount === 1) {
          logger().info('[ElementResolver] PlaywrightLocator resolved after auto-fill retry', {
            locator: locatorSource,
            waitedMs: Date.now() - retryStart,
          });
          const validated = await validateAndReturn(
            locator,
            'PlaywrightLocator (auto-fill retry)'
          );
          if (validated) return validated;
          break;
        } else if (retryCount > 1) {
          break; // Multiple matches — stop retrying, fall through
        }
      } catch {
        break; // Error during retry — stop
      }
    }
    return null;
  };

  // Try playwrightLocator first (highest confidence — authored by selector agent)
  if (selectors.playwrightLocator) {
    try {
      const variableValues = options?.variableValues ?? {};
      const locator = evaluateLocatorChain(page, selectors.playwrightLocator, variableValues);
      const count = await Promise.race([
        locator.count(),
        new Promise<number>((_, reject) =>
          setTimeout(
            () => reject(new Error('PlaywrightLocator count timed out')),
            RESOLVE_TIMEOUT_MS
          )
        ),
      ]);
      if (count === 1) {
        const validated = await validateAndReturn(locator, 'PlaywrightLocator (agentic)');
        if (validated) return validated;
      } else if (count > 1) {
        // V1: In strict mode, multi-match is an immediate failure — don't guess with .first()
        if (strictMode) {
          logger().warn('[ElementResolver] [strict] PlaywrightLocator matched multiple elements', {
            strictMode: true,
            rejection: 'multi_match',
            selectorDesc: 'PlaywrightLocator (agentic)',
            matchCount: count,
            locator: selectors.playwrightLocator,
          });
          errors.push(`PlaywrightLocator matched ${count} elements`);
        } else {
          // Try first visible element
          const first = locator.first();
          const isVis = await first.isVisible().catch(() => false);
          if (isVis) {
            const validated = await validateAndReturn(
              first,
              'PlaywrightLocator (agentic, first visible)'
            );
            if (validated) return validated;
          }
          errors.push(`PlaywrightLocator matched ${count} elements`);
        }
      } else {
        // V3: In strict mode, don't strip filters — they were authored for a reason
        if (!strictMode) {
          // 0 matches — try stripping .filter() clauses (auto-fill may not have propagated)
          const stripped = stripFilterClauses(selectors.playwrightLocator!);
          if (stripped) {
            try {
              const strippedLocator = evaluateLocatorChain(page, stripped, variableValues);
              const strippedCount = await Promise.race([
                strippedLocator.count(),
                new Promise<number>((_, reject) =>
                  setTimeout(
                    () => reject(new Error('Stripped locator count timed out')),
                    RESOLVE_TIMEOUT_MS
                  )
                ),
              ]);
              if (strippedCount === 1) {
                logger().warn('[ElementResolver] PlaywrightLocator resolved after stripping filter', {
                  original: selectors.playwrightLocator,
                  stripped,
                });
                const validated = await validateAndReturn(
                  strippedLocator,
                  'PlaywrightLocator (filter-stripped)'
                );
                if (validated) return validated;
              }
            } catch {
              // Stripped locator also failed — fall through
            }
          }
        } else {
          logger().warn('[ElementResolver] [strict] Skipping filter stripping for PlaywrightLocator', {
            strictMode: true,
            rejection: 'filter_stripped',
            locator: selectors.playwrightLocator,
          });
        }

        if (!hasAlternatePlaywrightLocator) {
          const retried = await retryPrimaryPlaywrightLocator(
            locator,
            selectors.playwrightLocator
          );
          if (retried) return retried;
        } else {
          primaryPlaywrightLocatorForRetry = locator;
          shouldRetryPrimaryAfterCandidates = true;
        }

        errors.push('PlaywrightLocator matched 0 elements');
      }
    } catch (err) {
      errors.push(`PlaywrightLocator failed: ${err instanceof Error ? err.message : String(err)}`);
    }

    // V14: In strict mode, if playwrightLocator exists and failed, don't fall through
    // to weaker heuristic selectors that may match a different element
    if (strictMode) {
      logger().warn('[ElementResolver] [strict] PlaywrightLocator failed, not falling through to heuristic selectors', {
        strictMode: true,
        rejection: 'locator_fallback_blocked',
        locator: selectors.playwrightLocator,
        errors,
      });
    }
    // Fall through to other strategies (only in non-strict mode)
  }

  const triedCandidateLocators = new Set<string>(
    selectors.playwrightLocator ? [selectors.playwrightLocator] : []
  );
  if (selectors.playwrightLocatorCandidates) {
    const candidateEntries = Object.entries(selectors.playwrightLocatorCandidates).filter(
      (entry): entry is [string, { locator: string }] =>
        typeof entry[1]?.locator === 'string' &&
        entry[1].locator.trim().length > 0 &&
        !triedCandidateLocators.has(entry[1].locator)
    );

    for (const [author, candidate] of candidateEntries) {
      triedCandidateLocators.add(candidate.locator);
      const selectorDesc = `PlaywrightLocator candidate (${author})`;
      try {
        const variableValues = options?.variableValues ?? {};
        const locator = evaluateLocatorChain(page, candidate.locator, variableValues);
        const count = await Promise.race([
          locator.count(),
          new Promise<number>((_, reject) =>
            setTimeout(
              () => reject(new Error(`${selectorDesc} count timed out`)),
              RESOLVE_TIMEOUT_MS
            )
          ),
        ]);

        if (count === 1) {
          const validated = await validateAndReturn(locator, selectorDesc);
          if (validated) return validated;
        } else if (count > 1) {
          if (strictMode) {
            logger().warn('[ElementResolver] [strict] PlaywrightLocator candidate matched multiple elements', {
              strictMode: true,
              rejection: 'multi_match',
              selectorDesc,
              matchCount: count,
              locator: candidate.locator,
            });
            errors.push(`${selectorDesc} matched ${count} elements`);
          } else {
            const first = locator.first();
            const isVis = await first.isVisible().catch(() => false);
            if (isVis) {
              const validated = await validateAndReturn(
                first,
                `${selectorDesc} (first visible)`
              );
              if (validated) return validated;
            }
            errors.push(`${selectorDesc} matched ${count} elements`);
          }
        } else {
          errors.push(`${selectorDesc} matched 0 elements`);
        }
      } catch (err) {
        errors.push(
          `${selectorDesc} failed: ${err instanceof Error ? err.message : String(err)}`
        );
      }
    }
  }

  if (
    shouldRetryPrimaryAfterCandidates &&
    primaryPlaywrightLocatorForRetry &&
    selectors.playwrightLocator
  ) {
    const retried = await retryPrimaryPlaywrightLocator(
      primaryPlaywrightLocatorForRetry,
      selectors.playwrightLocator
    );
    if (retried) return retried;
  }

  // V14: In strict mode, if playwrightLocator was present and failed, skip all heuristic fallbacks.
  // Heuristic selectors are less specific and may match a different element.
  const skipHeuristicFallbacks = strictMode && !!selectors.playwrightLocator;

  // Try data-testid selector first (across all contexts)
  if (selectors.testId && !skipHeuristicFallbacks) {
    for (const { name: contextName, context } of contexts) {
      try {
        const locator = context.getByTestId(selectors.testId);
        const count = await Promise.race([
          locator.count(),
          new Promise<number>((_, reject) =>
            setTimeout(() => reject(new Error('TestId count timed out')), RESOLVE_TIMEOUT_MS)
          ),
        ]);
        // V2: Split single-match from multi-match. Single match is always accepted.
        // Multi-match: strict mode fails, non-strict picks .first() (legacy behavior).
        if (count === 1) {
          const validated = await validateAndReturn(locator.first(), `TestId selector (in ${contextName})`);
          if (validated) return validated;
        } else if (count > 1) {
          if (strictMode) {
            logger().warn('[ElementResolver] [strict] TestId matched multiple elements', {
              strictMode: true,
              rejection: 'multi_match',
              selectorDesc: `TestId ("${selectors.testId}")`,
              matchCount: count,
            });
            errors.push(`TestId selector ("${selectors.testId}") matched ${count} elements`);
          } else {
            const first = locator.first();
            const validated = await validateAndReturn(first, `TestId selector (in ${contextName})`);
            if (validated) return validated;
          }
        }
      } catch (err) {
        if (contextName === 'page') {
          errors.push(
            `TestId selector failed: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      }
    }

    if (!errors.some((e) => e.includes('TestId selector'))) {
      errors.push(`TestId selector ("${selectors.testId}") found no matches`);
    }
  }

  // Skip nameless role selectors when a CSS ID selector is available — the role
  // selector with only matchIndex is too fragile for inputs without accessible names
  const hasNamelessRole = selectors.role && !selectors.role.name;
  const hasNoParentScope =
    !selectors.parentContext?.role &&
    !selectors.parentContext?.css &&
    !selectors.parentContext?.parentChain?.length;
  const hasCSSFallback = !!selectors.css;
  const skipFragileRole = hasNamelessRole && hasNoParentScope && hasCSSFallback;

  // Try role-based selector (across all contexts)
  if (skipFragileRole) {
    logger().debug('[ElementResolver] Skipping nameless role selector in favor of CSS fallback', {
      role: selectors.role!.role,
      css: selectors.css,
    });
  }
  if (selectors.role && !skipFragileRole && !skipHeuristicFallbacks) {
    for (const { name: contextName, context } of contexts) {
      try {
        const result = await tryRoleSelector(context, selectors, contextName, strictMode);
        if (result) {
          const validated = await validateAndReturn(result.locator, result.selectorDesc);
          if (validated) return validated;
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        errors.push(`Role selector failed in ${contextName}: ${msg}`);
      }
    }

    if (errors.length === 0) {
      errors.push(
        `Role selector (${selectors.role.role}${selectors.role.name ? `: "${selectors.role.name}"` : ''}) found no matches in page or frames`
      );
    }

    // CSS-based role fallback: when getByRole fails, try [role="..."] with containsText filter.
    // Non-queryable roles are excluded: tryRoleSelector already resolved them through the
    // tag approximation (which includes the explicit [role=...] form) with fail-closed
    // innermost matching — this heuristic's first-visible pick must not reopen ambiguity.
    if (selectors.containsText?.length && !NON_QUERYABLE_ROLES.has(selectors.role.role)) {
      logger().debug(
        '[ElementResolver] Trying CSS-based role selector fallback with containsText',
        {
          role: selectors.role.role,
          containsText: selectors.containsText,
        }
      );

      for (const { name: contextName, context } of contexts) {
        try {
          let cssRoleLocator = context.locator(`[role="${selectors.role.role}"]`);
          for (const textPart of selectors.containsText) {
            if (textPart) {
              cssRoleLocator = cssRoleLocator.filter({ hasText: textPart });
            }
          }

          const count = await Promise.race([
            cssRoleLocator.count(),
            new Promise<number>((_, reject) =>
              setTimeout(() => reject(new Error('CSS role count timed out')), RESOLVE_TIMEOUT_MS)
            ),
          ]);

          if (count > 0) {
            const selectorDesc = `CSS role selector ([role="${selectors.role.role}"] with containsText in ${contextName})`;

            if (count > 1) {
              logger().debug('[ElementResolver] Multiple CSS role matches, finding visible', {
                count,
              });
              const visibleElement = await findFirstVisibleElement(cssRoleLocator, count);
              if (visibleElement) {
                const validated = await validateAndReturn(
                  visibleElement,
                  `${selectorDesc} [visible]`
                );
                if (validated) return validated;
              }
            }

            const first = cssRoleLocator.first();
            const validated = await validateAndReturn(first, selectorDesc);
            if (validated) return validated;
          }
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          logger().debug('[ElementResolver] CSS role selector fallback failed', {
            context: contextName,
            error: msg,
          });
        }
      }
    }
  }

  // Try text selector (page only - frames already covered by role)
  if (selectors.text && !skipHeuristicFallbacks) {
    const normalizedText = normalizeMatchText(selectors.text.text);
    const { exact } = selectors.text;
    if (!normalizedText) {
      logger().debug('[ElementResolver] Skipping text selector due to empty normalized text');
      errors.push('Text selector skipped due to empty normalized text');
    } else {
      // For select elements, try getByLabel first (common pattern for labeled selects)
      if (requireSelect) {
        const selectScopedContexts = getParentScopedContexts(page, selectors.parentContext);
        for (const { searchCtx, scopeLabel } of selectScopedContexts) {
          try {
            const labelLocator = searchCtx.getByLabel(normalizedText, { exact: false });
            const count = await Promise.race([
              labelLocator.count(),
              new Promise<number>((_, reject) =>
                setTimeout(() => reject(new Error('Label count timed out')), RESOLVE_TIMEOUT_MS)
              ),
            ]);
            if (count > 0) {
              const first = labelLocator.first();
              const validated = await validateAndReturn(first, `Label selector${scopeLabel}`);
              if (validated) return validated;
            }
          } catch (err) {
            errors.push(
              `Label selector${scopeLabel} failed: ${err instanceof Error ? err.message : String(err)}`
            );
          }

          // Try finding a select associated with text via structure
          try {
            const selectNearText = searchCtx.locator(`select`).filter({
              has: searchCtx.locator(`..`).filter({ hasText: normalizedText }),
            });
            const selectCount = await Promise.race([
              selectNearText.count(),
              new Promise<number>((_, reject) =>
                setTimeout(
                  () => reject(new Error('Select near text timed out')),
                  RESOLVE_TIMEOUT_MS
                )
              ),
            ]);
            if (selectCount > 0) {
              await verifyElementReady(selectNearText.first(), `Select near text${scopeLabel}`, true, undefined, strictMode);
              return selectNearText.first();
            }
          } catch {
            // Ignore - just a fallback attempt
          }
        }
      }

      // Build list of text variants to try: full text first, then primary text if different
      const textVariants: Array<{ searchText: string; variant: string }> = [
        { searchText: normalizedText, variant: '' },
      ];

      const primaryText = extractPrimaryText(normalizedText);
      if (primaryText) {
        textVariants.push({ searchText: primaryText, variant: '[partial]' });
      }

      // Try text selector variants in page and frames
      let fallbackLocator: { locator: Locator; desc: string } | null = null;

      for (const { searchText, variant } of textVariants) {
        for (const { name: contextName, context } of contexts) {
          const scopedContexts = getParentScopedContexts(context, selectors.parentContext);
          for (const { searchCtx, scopeLabel } of scopedContexts) {
            try {
              const locator = searchCtx.getByText(searchText, { exact: exact ?? false });
              const count = await Promise.race([
                locator.count(),
                new Promise<number>((_, reject) =>
                  setTimeout(() => reject(new Error('Text count timed out')), RESOLVE_TIMEOUT_MS)
                ),
              ]);
              if (count > 0) {
                const selectorDesc = `Text selector ("${searchText}"${variant ? ` ${variant}` : ''}${scopeLabel} in ${contextName})`;

                if (scopeLabel) {
                  logger().debug('[ElementResolver] Text selector parent-scoped', {
                    parentRole: selectors.parentContext?.role,
                    searchText,
                    scopedCount: count,
                    scopeLabel,
                  });
                }

                if (count > 1) {
                  let disambigLocator = locator;
                  let disambigCount = count;

                  logger().debug('[ElementResolver] Multiple text matches, trying disambiguation', {
                    text: searchText,
                    count,
                    context: contextName,
                    variant,
                    scopeLabel,
                    hasItemIndex: selectors.itemIndex !== undefined,
                    hasRowIndex: selectors.rowIndex !== undefined,
                    hasMatchIndex: selectors.parentContext?.matchIndex !== undefined,
                  });

                  // Strategy 0: Auto-scope to visible dialog when no parentContext
                  if (!selectors.parentContext && !scopeLabel) {
                    const dialogScoped = await filterToVisibleDialog(
                      page,
                      disambigLocator,
                      disambigCount
                    );
                    if (dialogScoped && dialogScoped.count === 1) {
                      const validated = await validateAndReturn(
                        dialogScoped.locator.first(),
                        `${selectorDesc} [dialog-scoped]`
                      );
                      if (validated) return validated;
                    } else if (dialogScoped && dialogScoped.count > 0 && !strictMode) {
                      // V10: In strict mode, only accept dialog scoping if it narrows to exactly 1
                      disambigLocator = dialogScoped.locator;
                      disambigCount = dialogScoped.count;
                    }
                  }

                  // Strategy 1: Try data attribute matching (most deterministic)
                  if (
                    selectors.itemIndex !== undefined ||
                    selectors.rowIndex !== undefined ||
                    selectors.automationId
                  ) {
                    try {
                      const allAttrs = await extractDisambiguationAttrsBatch(
                        disambigLocator,
                        disambigCount
                      );
                      for (let i = 0; i < allAttrs.length; i++) {
                        const attrs = allAttrs[i];
                        const matches =
                          (selectors.itemIndex !== undefined &&
                            attrs.itemIndex === selectors.itemIndex) ||
                          (selectors.rowIndex !== undefined &&
                            attrs.rowIndex === selectors.rowIndex) ||
                          (selectors.automationId && attrs.automationId === selectors.automationId);

                        if (matches) {
                          const nth = disambigLocator.nth(i);
                          // Validate role compatibility when role selector was attempted
                          if (
                            selectors.role &&
                            !(await isRoleCompatible(nth, selectors.role.role))
                          ) {
                            logger().warn(
                              '[ElementResolver] Text match rejected - incompatible role (data-attr)',
                              {
                                expectedRole: selectors.role.role,
                                index: i,
                              }
                            );
                            continue;
                          }
                          logger().debug(
                            '[ElementResolver] Found text element by data attributes',
                            { index: i, attrs }
                          );
                          const validated = await validateAndReturn(
                            nth,
                            `${selectorDesc} [data-attr]`
                          );
                          if (validated) return validated;
                        }
                      }
                    } catch {
                      // Fall through to next strategy
                    }
                  }

                  // Strategy 2: Use matchIndex fallback
                  if (selectors.parentContext?.matchIndex !== undefined) {
                    const matchIndex = selectors.parentContext.matchIndex;
                    if (matchIndex < disambigCount) {
                      const indexed = disambigLocator.nth(matchIndex);
                      // Validate role compatibility when role selector was attempted
                      if (
                        selectors.role &&
                        !(await isRoleCompatible(indexed, selectors.role.role))
                      ) {
                        logger().warn(
                          '[ElementResolver] Text match rejected - incompatible role (matchIndex)',
                          {
                            expectedRole: selectors.role.role,
                            matchIndex,
                          }
                        );
                      } else {
                        logger().debug('[ElementResolver] Using text selector matchIndex', {
                          matchIndex,
                        });
                        const validated = await validateAndReturn(
                          indexed,
                          `${selectorDesc} [index:${matchIndex}]`
                        );
                        if (validated) return validated;
                      }
                    }
                  }

                  // Strategy 2b: Prefer interactive element for click actions
                  // When text matches both a label and an interactive element (e.g. combobox),
                  // clicking the label won't open the combobox — prefer the interactive one.
                  if (actionType === 'click') {
                    const interactiveMatches: Locator[] = [];
                    for (let i = 0; i < Math.min(disambigCount, 10); i++) {
                      const nth = disambigLocator.nth(i);
                      const isVis = await nth.isVisible().catch(() => false);
                      if (!isVis) continue;
                      if (await isInteractiveElement(nth)) {
                        interactiveMatches.push(nth);
                      }
                    }
                    if (interactiveMatches.length === 1) {
                      logger().info(
                        '[ElementResolver] Text disambiguation: preferring interactive element for click action',
                        {
                          text: searchText,
                          count,
                          interactiveCount: 1,
                        }
                      );
                      const validated = await validateAndReturn(
                        interactiveMatches[0]!,
                        `${selectorDesc} [interactive-click]`
                      );
                      if (validated) return validated;
                    } else if (interactiveMatches.length > 1) {
                      logger().debug(
                        '[ElementResolver] Text disambiguation: multiple interactive elements, falling through',
                        {
                          text: searchText,
                          interactiveCount: interactiveMatches.length,
                        }
                      );
                    }
                  }

                  // Strategy 2c: Prefer typeable element for type actions
                  // When text matches both a label and a typeable element (e.g. textarea),
                  // typing into the label won't work — prefer the typeable one.
                  if (actionType === 'type') {
                    const typeableMatches: Locator[] = [];
                    for (let i = 0; i < Math.min(disambigCount, 10); i++) {
                      const nth = disambigLocator.nth(i);
                      const isVis = await nth.isVisible().catch(() => false);
                      if (!isVis) continue;
                      if (await isTypeableElement(nth)) {
                        typeableMatches.push(nth);
                      }
                    }
                    if (typeableMatches.length === 1) {
                      logger().info(
                        '[ElementResolver] Text disambiguation: preferring typeable element for type action',
                        {
                          text: searchText,
                          count,
                          typeableCount: 1,
                        }
                      );
                      const validated = await validateAndReturn(
                        typeableMatches[0]!,
                        `${selectorDesc} [typeable-type]`
                      );
                      if (validated) return validated;
                    } else if (typeableMatches.length > 1) {
                      logger().debug(
                        '[ElementResolver] Text disambiguation: multiple typeable elements, falling through',
                        {
                          text: searchText,
                          typeableCount: typeableMatches.length,
                        }
                      );
                    }
                  }

                  // Strategy 3: Find first visible element
                  const visibleElement = await findFirstVisibleElement(
                    disambigLocator,
                    disambigCount
                  );
                  if (visibleElement) {
                    // Validate role compatibility when role selector was attempted
                    if (
                      selectors.role &&
                      !(await isRoleCompatible(visibleElement, selectors.role.role))
                    ) {
                      logger().warn(
                        '[ElementResolver] Text match rejected - incompatible role (visible)',
                        {
                          expectedRole: selectors.role.role,
                        }
                      );
                    } else {
                      const validated = await validateAndReturn(
                        visibleElement,
                        `${selectorDesc} [visible]`
                      );
                      if (validated) return validated;
                    }
                  } else {
                    logger().debug(
                      '[ElementResolver] No visible elements found among matches, trying first anyway',
                      {
                        text: searchText,
                        count: disambigCount,
                      }
                    );
                  }
                }

                // Single match or fallback to first
                const first = locator.first();
                // Validate role compatibility when role selector was attempted
                if (selectors.role && !(await isRoleCompatible(first, selectors.role.role))) {
                  logger().warn('[ElementResolver] Text match rejected - incompatible role', {
                    expectedRole: selectors.role.role,
                    text: searchText,
                    context: contextName,
                  });
                  continue;
                }
                const validated = await validateAndReturn(first, selectorDesc);
                if (validated) return validated;
              }
            } catch (err) {
              const errorMsg = err instanceof Error ? err.message : String(err);
              if (contextName === 'page' && !variant) {
                errors.push(`Text selector failed: ${errorMsg}`);
              } else if (errorMsg.includes('not ready') || errorMsg.includes('Timeout')) {
                logger().debug('[ElementResolver] Text selector visibility check failed', {
                  text: searchText,
                  context: contextName,
                  variant,
                  error: errorMsg,
                });
                errors.push(
                  `Text selector (${searchText}${variant} in ${contextName}) visibility failed`
                );

                if (contextName.includes('iframe')) {
                  const fallbackSelector = `Text selector ("${searchText}"${variant ? ` ${variant}` : ''} in ${contextName})`;
                  const loc = context.getByText(searchText, { exact: false });
                  fallbackLocator = {
                    locator: loc.first(),
                    desc: `${fallbackSelector} [fallback]`,
                  };
                }
              }
            }
          }
        }
      }

      // If we have a fallback locator from iframe partial match, try it without strict visibility
      if (fallbackLocator && !requireSelect) {
        logger().info('[ElementResolver] Trying fallback locator from iframe partial match', {
          desc: fallbackLocator.desc,
        });
        try {
          const isReady = await verifyElementReady(
            fallbackLocator.locator,
            fallbackLocator.desc,
            false,
            undefined,
            strictMode
          );
          if (!isReady) {
            logger().warn(
              '[ElementResolver] Fallback locator visibility check failed, proceeding anyway'
            );
          }
          logger().debug('[ElementResolver] Resolved element via fallback', {
            selector: fallbackLocator.desc,
          });
          return fallbackLocator.locator;
        } catch (err) {
          errors.push(
            `Fallback locator failed: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      }

      if (!errors.some((e) => e.includes('Text selector'))) {
        errors.push(`Text selector ("${normalizedText}") found no matches`);
      }
    }
  }

  // Try ARIA label (page and frames)
  // Use exact matching to avoid substring false positives (e.g. "Location Code"
  // matching "Open menu for Location Code"). Falls back to inexact if exact finds nothing.
  if (selectors.ariaLabel && !skipHeuristicFallbacks) {
    for (const { name: contextName, context } of contexts) {
      try {
        let locator = context.getByLabel(selectors.ariaLabel, { exact: true });
        const exactCount = await Promise.race([
          locator.count(),
          new Promise<number>((_, reject) =>
            setTimeout(
              () => reject(new Error('ARIA label exact count timed out')),
              RESOLVE_TIMEOUT_MS
            )
          ),
        ]);
        if (exactCount === 0) {
          locator = context.getByLabel(selectors.ariaLabel);
        }
        const count = await Promise.race([
          locator.count(),
          new Promise<number>((_, reject) =>
            setTimeout(() => reject(new Error('ARIA label count timed out')), RESOLVE_TIMEOUT_MS)
          ),
        ]);
        if (count > 0) {
          const selectorDesc = `ARIA label selector ("${selectors.ariaLabel}" in ${contextName})`;

          if (count === 1) {
            const validated = await validateAndReturn(locator.first(), selectorDesc);
            if (validated) return validated;
          } else {
            // Multiple matches — try disambiguation strategies
            let ariaDisambigLocator = locator;
            let ariaDisambigCount = count;

            logger().debug('[ElementResolver] Multiple ARIA label matches, trying disambiguation', {
              ariaLabel: selectors.ariaLabel,
              count,
              context: contextName,
              hasChildFilter: !!selectors.childFilter,
              hasItemIndex: selectors.itemIndex !== undefined,
              hasMatchIndex: selectors.parentContext?.matchIndex !== undefined,
            });

            // Strategy 0: Auto-scope to visible dialog when no parentContext
            if (!selectors.parentContext) {
              const dialogScoped = await filterToVisibleDialog(
                page,
                ariaDisambigLocator,
                ariaDisambigCount
              );
              if (dialogScoped && dialogScoped.count === 1) {
                const validated = await validateAndReturn(
                  dialogScoped.locator.first(),
                  `${selectorDesc} [dialog-scoped]`
                );
                if (validated) return validated;
              } else if (dialogScoped && dialogScoped.count > 0 && !strictMode) {
                // V10: In strict mode, only accept dialog scoping if it narrows to exactly 1
                ariaDisambigLocator = dialogScoped.locator;
                ariaDisambigCount = dialogScoped.count;
              }
            }

            // Strategy 1: Try childFilter disambiguation (e.g., filter by child img alt text)
            if (selectors.childFilter) {
              const filtered = ariaDisambigLocator.filter({
                has: context.getByRole(
                  selectors.childFilter.role as Parameters<Page['getByRole']>[0],
                  { name: selectors.childFilter.name }
                ),
              });
              const filteredCount = await Promise.race([
                filtered.count(),
                new Promise<number>((_, reject) =>
                  setTimeout(
                    () => reject(new Error('childFilter count timed out')),
                    RESOLVE_TIMEOUT_MS
                  )
                ),
              ]);
              if (filteredCount === 1) {
                const validated = await validateAndReturn(
                  filtered.first(),
                  `${selectorDesc} [childFilter]`
                );
                if (validated) return validated;
              }
            }

            // Strategy 2: Try data attribute matching
            if (
              selectors.itemIndex !== undefined ||
              selectors.rowIndex !== undefined ||
              selectors.automationId
            ) {
              try {
                const allAttrs = await extractDisambiguationAttrsBatch(
                  ariaDisambigLocator,
                  ariaDisambigCount
                );
                for (let i = 0; i < allAttrs.length; i++) {
                  const attrs = allAttrs[i];
                  const matches =
                    (selectors.itemIndex !== undefined &&
                      attrs.itemIndex === selectors.itemIndex) ||
                    (selectors.rowIndex !== undefined && attrs.rowIndex === selectors.rowIndex) ||
                    (selectors.automationId && attrs.automationId === selectors.automationId);

                  if (matches) {
                    logger().debug(
                      '[ElementResolver] Found ARIA label element by data attributes',
                      { index: i, attrs }
                    );
                    const validated = await validateAndReturn(
                      ariaDisambigLocator.nth(i),
                      `${selectorDesc} [data-attr]`
                    );
                    if (validated) return validated;
                  }
                }
              } catch {
                // Fall through to next strategy
              }
            }

            // Strategy 3: Use matchIndex fallback
            if (selectors.parentContext?.matchIndex !== undefined) {
              const matchIndex = selectors.parentContext.matchIndex;
              if (matchIndex < ariaDisambigCount) {
                const indexed = ariaDisambigLocator.nth(matchIndex);
                logger().debug('[ElementResolver] Using ARIA label matchIndex', { matchIndex });
                const validated = await validateAndReturn(
                  indexed,
                  `${selectorDesc} [index:${matchIndex}]`
                );
                if (validated) return validated;
              }
            }

            // In verify mode, treat as selector miss so discoverParentContext
            // can try to find a narrower parent scope. In replay mode, pick the
            // first visible match as a best-effort (better than failing outright).
            if (mode === 'verify') {
              logger().warn(
                '[ElementResolver] No ARIA label disambiguation succeeded (verify mode, treating as miss)',
                {
                  ariaLabel: selectors.ariaLabel,
                  count: ariaDisambigCount,
                }
              );
            } else {
              const visibleElement = await findFirstVisibleElement(
                ariaDisambigLocator,
                ariaDisambigCount
              );
              if (visibleElement) {
                logger().warn(
                  '[ElementResolver] No ARIA label disambiguation succeeded, using first visible (replay mode)',
                  {
                    ariaLabel: selectors.ariaLabel,
                    count: ariaDisambigCount,
                  }
                );
                const validated = await validateAndReturn(
                  visibleElement,
                  `${selectorDesc} [visible]`
                );
                if (validated) return validated;
              }
              logger().warn('[ElementResolver] No ARIA label disambiguation succeeded', {
                ariaLabel: selectors.ariaLabel,
                count: ariaDisambigCount,
              });
            }
          }
        }
      } catch (err) {
        if (contextName === 'page') {
          errors.push(
            `ARIA label selector failed: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      }
    }

    if (!errors.some((e) => e.includes('ARIA label'))) {
      errors.push(`ARIA label selector ("${selectors.ariaLabel}") found no matches`);
    }
  }

  // Try placeholder text (page and frames)
  if (selectors.placeholder && !skipHeuristicFallbacks) {
    for (const { name: contextName, context } of contexts) {
      try {
        const locator = context.getByPlaceholder(selectors.placeholder, { exact: false });
        const count = await Promise.race([
          locator.count(),
          new Promise<number>((_, reject) =>
            setTimeout(() => reject(new Error('Placeholder count timed out')), RESOLVE_TIMEOUT_MS)
          ),
        ]);
        if (count > 0) {
          const first = locator.first();
          const validated = await validateAndReturn(
            first,
            `Placeholder selector (in ${contextName})`
          );
          if (validated) return validated;
        }
      } catch (err) {
        if (contextName === 'page') {
          errors.push(
            `Placeholder selector failed: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      }
    }

    if (!errors.some((e) => e.includes('Placeholder selector'))) {
      errors.push(`Placeholder selector ("${selectors.placeholder}") found no matches`);
    }
  }

  // Try CSS selector as a last resort. Search the same page+frame `contexts` as
  // the role/text paths (so a target captured inside an iframe resolves in the
  // frame, not the top page), and honor the full parentContext (role /
  // containsText / css scopes) within each — mirroring the text-selector path.
  // Without this, a `css` anchor like `[data-field="…"]` that's only unique
  // within an ancestor would match globally and pick the wrong element.
  if (selectors.css && !skipHeuristicFallbacks) {
    const pc = selectors.parentContext;
    // A scoped css anchor (stable-id / row-variable / ancestor) exists ONLY to
    // single out one target within its scope. For those, `count !== 1` is an
    // ambiguity — treat it as a miss (fall back to the agent), never pick-first
    // and never widen to the global page. Unscoped, globally-unique css
    // predicates (semanticClass / iconClass / imgSrc) keep the legacy
    // pick-first behavior.
    const scoped = Boolean(pc && (pc.css || pc.role || pc.containsText || pc.parentChain?.length));

    for (const { name: contextName, context } of contexts) {
      const cssScopes = getParentScopedContexts(context, selectors.parentContext);
      for (const { searchCtx, scopeLabel } of cssScopes) {
        // Never search the unscoped/global page for a scoped anchor.
        if (scoped && scopeLabel === '') continue;
        try {
          const locator = searchCtx.locator(selectors.css);
          const count = await Promise.race([
            locator.count(),
            new Promise<number>((_, reject) =>
              setTimeout(() => reject(new Error('CSS count timed out')), RESOLVE_TIMEOUT_MS)
            ),
          ]);

          if (scoped) {
            if (count === 1) {
              const validated = await validateAndReturn(
                locator.first(),
                `CSS selector${scopeLabel} (in ${contextName})`
              );
              if (validated) return validated;
            } else if (count > 1) {
              errors.push(
                `CSS selector${scopeLabel} (in ${contextName}) ambiguous — ${count} matches; treating as miss`
              );
            }
            continue;
          }

          if (count > 1) {
            // Multiple CSS matches — try visible element first (handles hidden duplicates)
            const visibleElement = await findFirstVisibleElement(locator, count);
            if (visibleElement) {
              const validated = await validateAndReturn(
                visibleElement,
                `CSS selector (in ${contextName}) [visible]`
              );
              if (validated) return validated;
            }
          }
          if (count > 0) {
            const validated = await validateAndReturn(locator.first(), `CSS selector (in ${contextName})`);
            if (validated) return validated;
          }
        } catch (err) {
          errors.push(
            `CSS selector${scopeLabel} (in ${contextName}) failed: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      }
    }
    errors.push(`CSS selector ("${selectors.css}") found no matches`);
  }

  // Structural fallback: for dynamic text selectors, try role + hasText with current value
  if (selectors.dynamicText && options?.variableValues && selectors.role && !requireSelect && !skipHeuristicFallbacks) {
    const { key } = selectors.dynamicText;
    const currentValue = options.variableValues[key];

    if (currentValue) {
      logger().debug('[ElementResolver] Trying structural fallback for dynamic text', {
        role: selectors.role.role,
        currentValue,
      });

      for (const { name: contextName, context } of contexts) {
        try {
          const roleLocator = context.getByRole(
            selectors.role.role as Parameters<Page['getByRole']>[0]
          );
          const filtered = roleLocator.filter({ hasText: currentValue });
          const count = await Promise.race([
            filtered.count(),
            new Promise<number>((_, reject) =>
              setTimeout(
                () => reject(new Error('Dynamic fallback count timed out')),
                RESOLVE_TIMEOUT_MS
              )
            ),
          ]);

          if (count > 0) {
            const first = filtered.first();
            const isReady = await verifyElementReady(
              first,
              `Dynamic fallback (${selectors.role.role} hasText:${currentValue} in ${contextName})`,
              false,
              undefined,
              strictMode
            );
            if (isReady) {
              logger().info('[ElementResolver] Resolved via dynamic text structural fallback', {
                role: selectors.role.role,
                currentValue,
                context: contextName,
              });
              return first;
            }
          }
        } catch (err) {
          logger().debug('[ElementResolver] Dynamic fallback failed for context', {
            context: contextName,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
    }
  }

  // Resolution failed — build structured diagnostics
  const totalDurationMs = Date.now() - resolveStartMs;
  const strategies: Array<{ type: string; tried: boolean; result: string }> = [
    {
      type: 'testId',
      tried: !!selectors.testId,
      result: selectors.testId
        ? (errors.find((e) => e.includes('TestId')) ?? 'no matches')
        : 'not available',
    },
    {
      type: 'role',
      tried: !!selectors.role,
      result: selectors.role
        ? (errors.find((e) => e.includes('Role selector')) ?? 'no matches')
        : 'not available',
    },
    {
      type: 'text',
      tried: !!selectors.text,
      result: selectors.text
        ? (errors.find((e) => e.includes('Text selector')) ?? 'no matches')
        : 'not available',
    },
    {
      type: 'ariaLabel',
      tried: !!selectors.ariaLabel,
      result: selectors.ariaLabel
        ? (errors.find((e) => e.includes('ARIA label')) ?? 'no matches')
        : 'not available',
    },
    {
      type: 'placeholder',
      tried: !!selectors.placeholder,
      result: selectors.placeholder
        ? (errors.find((e) => e.includes('Placeholder')) ?? 'no matches')
        : 'not available',
    },
    {
      type: 'css',
      tried: !!selectors.css,
      result: selectors.css
        ? (errors.find((e) => e.includes('CSS selector')) ?? 'no matches')
        : 'not available',
    },
  ];

  const errorMsg = `Could not find ${elementTypeName} with any selector strategy. Tried: ${errors.join('; ')}`;

  if (mode === 'verify') {
    logger().debug('[ElementResolver] Element resolution failed (verify mode)', {
      strategies: strategies.filter((s) => s.tried),
      totalDurationMs,
    });
    return null;
  }

  logger().error('[ElementResolver] Element resolution failed', {
    strategies,
    totalDurationMs,
    selectorSummary: {
      testId: selectors.testId,
      role: selectors.role?.role,
      roleName: selectors.role?.name,
      parentContext: selectors.parentContext?.role,
      parentContextCss: selectors.parentContext?.css,
      text: selectors.text?.text,
      ariaLabel: selectors.ariaLabel,
    },
  });
  throw new Error(errorMsg);
}
